import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { HandicapHCP } from "@data-types/sport-stats/SportStatsTypes";
import RecentGames from "../RecentGames";

import SegmentedGraph from "../SegmentedGraph";

export default function HandicapStatistics(props: {
	homeTeam: HandicapHCP["data"];
	awayTeam: HandicapHCP["data"];
}) {
	const { t } = useTranslation();
	const scores = useMemo(() => {
		// combine scores from both teams
		const combined = props.homeTeam?.commonGoalDifference?.concat(
			props.awayTeam?.commonGoalDifference || []
		);
		const scores = combined?.map((item) => item.scoreDifference);

		if (scores) {
			// filter out duplicates
			return scores.filter(
				(value, index) => scores.indexOf(value) == index
			);
		}
	}, []);

	const getFrequencyPerScore = (
		score: number,
		team: "homeTeam" | "awayTeam",
		isCommonGoalDiff?: boolean
	) => {
		if (isCommonGoalDiff) {
			return props[team]?.commonGoalDifference?.find(
				(item) => item.scoreDifference == score
			)?.frequency;
		}
		return props[team]?.goalDifference?.find(
			(item) => item.scoreDifference == score
		)?.frequency;
	};

	if (!scores) {
		console.error("commonGoalDifference is null.");
		return null;
	}

	return (
		<>
			<RecentGames
				homeTeamEventCount={props.homeTeam?.eventCount}
				awayTeamEventCount={props.awayTeam?.eventCount}
			/>

			<SegmentedGraph
				title={t(
					"EVENT.STATISTICS.BETTING_TYPE_STATS.COMMON_GOALS_DIFF"
				)}
				isPercentage
				hideAwayTitle
				hideHomeTitle
				hideDrawTitle
				data={{
					homeLabel: scores[0]?.toString(),
					homeValue: getFrequencyPerScore(
						scores[0],
						"homeTeam",
						true
					),
					dwawLabel: scores[1]?.toString(),
					drawValue: getFrequencyPerScore(
						scores[1],
						"homeTeam",
						true
					),
					awayLabel: scores[2]?.toString(),
					awayValue: getFrequencyPerScore(
						scores[2],
						"homeTeam",
						true
					),
				}}
				className="u-mar--top--xsml"
			/>

			<div className="card card--stats u-mar--top--xsml">
				<div className="card__header card--stats__header">
					<p className="card--stats__title">
						{t("EVENT.STATISTICS.BETTING_TYPE_STATS.GOALS_DIFF")}
					</p>
				</div>

				{scores.map((scoreNum, index) => {
					return (
						<GoalsDifference
							score={scoreNum}
							homeFrequency={getFrequencyPerScore(
								scoreNum,
								"homeTeam"
							)}
							awayFrequency={getFrequencyPerScore(
								scoreNum,
								"awayTeam"
							)}
							lastItem={scores.length === index + 1}
						/>
					);
				})}
			</div>
		</>
	);
}

function GoalsDifference({
	score,
	homeFrequency,
	awayFrequency,
	lastItem,
}: {
	score: number;
	homeFrequency?: number;
	awayFrequency?: number;
	lastItem?: boolean;
}) {
	const { t } = useTranslation();

	return (
		<>
			<div className="card__body card--stats__body--border">
				<div className="card--stats__row">
					<div className="card--stats__col card--stats__col--middle">
						<div className="card--stats__label u-type--wgt--bold">
							{score}
						</div>
					</div>
				</div>

				{homeFrequency && (
					<>
						<div className="card--stats__row">
							<div className="card--stats__col card--stats__col--left">
								<div className="card--stats__label">
									{t("EVENT.OFFER.COMPETITOR_HOME")}
								</div>
							</div>
							<div className="card--stats__comparison card--stats__comparison--right">
								<span className="card--stats__value is-home">
									{homeFrequency}%
								</span>
							</div>
						</div>
						<div className="card--stats__row">
							<div className="card--stats__col card--stats__col--left">
								<div className="chart chart--left is-home">
									<progress
										id="chartHome"
										className="chart__progress"
										value={homeFrequency || 0}
										max={100}
									/>
								</div>
							</div>
						</div>
					</>
				)}

				{awayFrequency && (
					<>
						<div className="card--stats__row">
							<div className="card--stats__col card--stats__col--left">
								<div className="card--stats__label">
									{t("EVENT.OFFER.COMPETITOR_AWAY")}
								</div>
							</div>
							<div className="card--stats__comparison card--stats__comparison--right">
								<span className="card--stats__value is-away">
									{awayFrequency}%
								</span>
							</div>
						</div>
						<div className="card--stats__row">
							<div className="card--stats__col card--stats__col--left">
								<div className="chart chart--left is-away">
									<progress
										id="chartHome"
										className="chart__progress"
										value={awayFrequency || 0}
										max={100}
									/>
								</div>
							</div>
						</div>
					</>
				)}
			</div>
		</>
	);
}
